<!-- ══════════════════════════════════════════════════════
     ASSESSMENT LANDING — assessment-landing.component.html
     Prefix: al-   |  No orange   |  Sehyog sh- token colours
══════════════════════════════════════════════════════ -->

<!-- ══ HERO — 3-column desktop: [Image | Copy | Form] ══ -->
<section class="al-hero">
  <div class="al-hero-inner">

    <!-- COL 1: Full-height image -->
    <div class="al-hero-img-col">
      <div class="al-hero-img-wrap">
        <img class="al-hero-img"
             src="https://images.pexels.com/photos/8423093/pexels-photo-8423093.jpeg"
             alt="UK-Wide End of Year Assessment"
             onerror="this.style.display='none'" />
      </div>
    </div>

    <!-- COL 2: Headline + copy -->
    <div class="al-hero-copy">
  

      <h1 class="al-h1">
        UK-Wide End of Year<br/>
        <span class="al-h1-hi">Assessment</span><br/>
        for Year 3, 4 &amp; 5
      </h1>

      <p class="al-hero-sub">
        A structured evaluation for Years 3 to 5 to spot learning
        leaks and eliminate exam panic — before it costs them a
        grammar school place.
      </p>
     

      <!-- powered-by bar sits nicely under the copy on desktop -->
      <div class="al-rar-inline">
        <span>Powered by Sehyog's proprietary
          <strong>RAR &amp; AI-Driven Student Feedback Dashboard</strong>
        </span>
      </div>
    </div>

    <!-- COL 3: Registration form -->
    <div id="al-register" class="al-form-wrap">
      <div class="al-form-card">

        <div class="al-form-success" *ngIf="formSubmitted">
          <div class="al-success-icon">✓</div>
          <h3>You're Registered!</h3>
          <p>Check your inbox for confirmation details and your webinar invite.</p>
        </div>

        <ng-container *ngIf="!formSubmitted">
          <h3 class="al-form-title">Register for a Detailed Assessment</h3>
          <p class="al-form-sub">Free to enter. Results powered by AI. Takes 90 seconds.</p>

          <div class="al-field">
            <input class="al-input" type="text" placeholder="Parent's Full Name" [(ngModel)]="form.parentName" />
          </div>
          <div class="al-field">
            <input class="al-input" type="email" placeholder="Email Address" [(ngModel)]="form.email" />
          </div>
          <div class="al-field">
            <input class="al-input" type="tel" placeholder="Phone Number (incl. country code)" [(ngModel)]="form.phone" />
          </div>
          <div class="al-field">
            <select class="al-select" [(ngModel)]="form.yearGroup">
              <option value="">Child's Current School Year</option>
              <option value="Year 3">Year 3</option>
              <option value="Year 4">Year 4</option>
              <option value="Year 5">Year 5</option>
            </select>
          </div>

          <div class="al-form-error" *ngIf="formError">
            Please fill in all fields before submitting.
          </div>

          <button class="al-btn-submit" (click)="onSubmit()">
            Register for a Detailed Assessment →
          </button>

          <div class="al-form-trust">
            <span class="al-trust-item">🔒 Secure</span>
            <span class="al-trust-item">✅ 100% Free</span>
            <span class="al-trust-item">📧 Instant Confirmation</span>
          </div>
        </ng-container>

      </div>
    </div>
  </div>
</section>

<!-- ══ TRUST STRIP ══ -->
<div class="al-trust-strip">
  <div class="al-trust-inner">
    <div class="al-trust-stat">
      <div class="al-trust-icon">👨‍👩‍👧</div>
      <div class="al-trust-text"><strong>4,800+</strong><span>Families registered</span></div>
    </div>
    <div class="al-trust-stat">
      <div class="al-trust-icon">🏫</div>
      <div class="al-trust-text"><strong>93%</strong><span>Grammar school pass rate</span></div>
    </div>
    <div class="al-trust-stat">
      <div class="al-trust-icon">🤖</div>
      <div class="al-trust-text"><strong>AI-Driven</strong><span>RAR feedback dashboard</span></div>
    </div>
    <div class="al-trust-stat">
      <div class="al-trust-icon">⭐</div>
      <div class="al-trust-text"><strong>4.9 / 5</strong><span>Parent satisfaction score</span></div>
    </div>
  </div>
</div>

<!-- ══ SECTION 2: DIAGNOSTIC PILLARS ══ -->
<section class="al-section al-pillars-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">The 3 Diagnostic Pillars</div>
      <h2 class="al-heading">We Don't Just Mark Papers.<br/>We Decode Performance.</h2>
      <p class="al-sub">Most assessments tell you a score. Sehyog's diagnostic engine tells you exactly <em>why</em> that score happened — and what to do about it.</p>
    </div>
    <div class="al-rar-bar">
      <span class="al-rar-dot"></span>
      <span class="al-rar-text">
        Powered by Sehyog Academy's proprietary
        <strong>RAR &amp; AI-Driven Student Feedback Report Dashboard</strong>
      </span>
    </div>
    <div class="al-pillars-grid">
      <div class="al-pillar-card" [ngClass]="pillar.cls" *ngFor="let pillar of pillars">
        <div class="al-pillar-num">{{pillar.num}}</div>
        <h3 class="al-pillar-title">{{pillar.title}}</h3>
        <p class="al-pillar-body">{{pillar.body}}</p>
        <span class="al-pillar-tag">{{pillar.tag}}</span>
      </div>
    </div>
  </div>
</section>

<!-- ══ ASSESSMENT STRUCTURE ══ -->
<section class="al-section al-structure-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label al-label-gold">Assessment Structure</div>
      <h2 class="al-heading al-heading-white">200 Marks. Two Papers. Full Picture.</h2>
      <p class="al-sub al-sub-faded">Mirroring the format of leading grammar school entrance exams so your child practises in a realistic, timed environment.</p>
    </div>
    <div class="al-structure-grid">
      <div class="al-structure-card" [ngClass]="sc.cls" *ngFor="let sc of structureCards">
        <div class="al-sc-num">{{sc.num}}</div>
        <h4 class="al-sc-title">{{sc.title}}</h4>
        <div class="al-sc-mark">{{sc.mark}}</div>
        <ul class="al-sc-topics">
          <li *ngFor="let t of sc.topics">{{t}}</li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- ══ HOW IT WORKS ══ -->
<section class="al-section al-hiw-section" id="al-how-it-works">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">How It Works</div>
      <h2 class="al-hiw-heading">
        From Registration to Results<br/>
        <span class="al-hiw-hi">in Four Simple Steps</span>
      </h2>
      <p class="al-sub al-sub-center">We've made the process frictionless — so you can focus on your child's progress, not the admin.</p>
    </div>
    <div class="al-hiw-grid">
      <div class="al-steps">
        <div class="al-step" *ngFor="let step of howSteps">
          <div class="al-step-num" [ngClass]="step.cls">{{step.num}}</div>
          <div class="al-step-content">
            <h5 class="al-step-title">{{step.title}}</h5>
            <p class="al-step-body">{{step.body}}</p>
          </div>
        </div>
      </div>
      <div class="al-webinar-card">
        <div class="al-webinar-free-badge">🎓 Free Webinar</div>
        <h3 class="al-webinar-title">Post-Assessment For Parents</h3>
        <div class="al-webinar-date">📅 Included free with every registration</div>
        <p class="al-webinar-desc">
          A dedicated live session hosted by Sehyog's lead tutors to walk through results patterns,
          common accuracy errors, and exactly what your child's data means for their 11+ journey.
        </p>
        <ul class="al-webinar-list">
          <li *ngFor="let item of webinarChecklist">{{item}}</li>
        </ul>
        <button class="al-webinar-btn" (click)="scrollToRegister()">Register Now →</button>
        <p class="al-webinar-note">*Cohort Places Are Strictly Limited</p>
      </div>
    </div>
  </div>
</section>

<!-- ══ WHAT YOUR CHILD WILL RECEIVE ══ -->
<section class="al-section al-receives-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">What Your Child Will Receive</div>
      <h2 class="al-heading">More Than Just a Mark</h2>
      <p class="al-sub">Every registered family receives a rich results package within 48 hours — built on Sehyog's AI and RAR engine, not generic automated scoring.</p>
    </div>
    <div class="al-receives-grid">
      <div class="al-receives-card" *ngFor="let rc of receivesCards">
        <div class="al-rc-icon">{{rc.icon}}</div>
        <div class="al-rc-text">
          <h5 class="al-rc-title">{{rc.title}}</h5>
          <p class="al-rc-body">{{rc.body}}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ══ FAQ ══ -->
<section class="al-section al-faq-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">FAQs</div>
      <h2 class="al-heading">Your Questions, Answered</h2>
    </div>
    <div class="al-faq-list">
      <div class="al-faq-item" [ngClass]="{'al-faq-open': faq.open}" *ngFor="let faq of faqs; let i = index">
        <div class="al-faq-q" (click)="toggleFaq(i)">
          <span>{{faq.q}}</span>
          <span class="al-faq-arrow">▾</span>
        </div>
        <div class="al-faq-a" [style.maxHeight]="faq.open ? '240px' : '0px'">
          <div class="al-faq-a-inner">{{faq.a}}</div>
        </div>
      </div>
    </div>
  </div>
</section>